// frontend/src/components/players/PDFViewer.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSecureMedia } from '../../services/mediaService';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PDF_JS_OPTIONS = {
  disableAutoFetch: true,
  disableStream: false,
  useWorkerFetch: true,
  useSystemFonts: true,
  cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.296/cmaps/',
  cMapPacked: true,
  standardFontDataUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.296/standard_fonts/',
  maxImageSize: 512 * 1024,
  enableXfa: false,
  stopAtErrors: false,
  fontExtraProperties: false,
  isEvalSupported: false,
  useStandardFontData: true,
  verbosity: 0,
};

const QUALITY_LEVELS = {
  low: { label: 'اقتصادی', dpi: 100 },
  medium: { label: 'متوسط', dpi: 150 },
  high: { label: 'بالا', dpi: 200 },
  ultra: { label: 'عالی', dpi: 300 },
};

const toPersian = (num) => {
  const persianDigits = '۰۱۲۳۴۵۶۷۸۹';
  return String(num).replace(/\d/g, d => persianDigits[parseInt(d)]);
};

const PDFViewer = ({ mediaId, fileUrl, title, productTitle }) => {
  const navigate = useNavigate();
  const headerTitle = productTitle || title || 'PDF';
  
  const { mediaUrl, isLoading: urlLoading, error: urlError, refetch: refetchMediaUrl } =
    useSecureMedia(mediaId);

  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [scale, setScale] = useState(1.2);
  const [isScannedPDF, setIsScannedPDF] = useState(null);
  const [quality, setQuality] = useState('medium');
  const [imageScale, setImageScale] = useState(1.2); // ✅ زوم تصویر
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const loadingTaskRef = useRef(null);
  const renderTaskRef = useRef(null);
  const isMounted = useRef(true);

  const directPdf = fileUrl || mediaUrl;
  const proxyUrl = directPdf?.startsWith('https://s3.afra.kubit.cloud/')
    ? `https://farzaneh.app/api/proxy/media/?url=${encodeURIComponent(directPdf)}`
    : directPdf;

  const effectiveMediaId = mediaId || 292;

  useEffect(() => {
    if (!proxyUrl || !effectiveMediaId) return;
    isMounted.current = true;

    const checkSize = async () => {
      try {
        setLoading(true);
        const headRes = await fetch(proxyUrl, { method: 'HEAD' });
        const contentLength = parseInt(headRes.headers.get('Content-Length') || '0');
        
        if (contentLength > 5 * 1024 * 1024 && effectiveMediaId) {
          setIsScannedPDF(true);
          const infoRes = await fetch(`/api/v1/products/pdf-info/${effectiveMediaId}/`);
          const info = await infoRes.json();
          setNumPages(info.numPages || 1);
          setLoading(false);
        } else {
          setIsScannedPDF(false);
          const loadingTask = pdfjsLib.getDocument({ url: proxyUrl, ...PDF_JS_OPTIONS });
          loadingTaskRef.current = loadingTask;
          const pdf = await loadingTask.promise;
          if (!isMounted.current) return;
          setPdfDoc(pdf);
          setNumPages(pdf.numPages);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted.current) {
          setIsScannedPDF(false);
          setError('خطا در بارگذاری فایل');
          setLoading(false);
        }
      }
    };

    checkSize();
    return () => { isMounted.current = false; if (loadingTaskRef.current) loadingTaskRef.current.destroy(); };
  }, [proxyUrl, effectiveMediaId]);

  useEffect(() => {
    if (!pdfDoc || isScannedPDF || !canvasRef.current) return;
    let cancelled = false;
    
    (async () => {
      try {
        if (renderTaskRef.current) { await renderTaskRef.current.cancel(); renderTaskRef.current = null; }
        const page = await pdfDoc.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 1 });
        const cw = canvasRef.current.parentElement?.clientWidth || 800;
        const ch = window.innerHeight - 200;
        const s = Math.max(0.5, Math.min(cw / viewport.width, ch / viewport.height, 2));
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const vs = page.getViewport({ scale: s * dpr });
        const canvas = canvasRef.current;
        canvas.width = vs.width;
        canvas.height = vs.height;
        canvas.style.width = `${vs.width / dpr}px`;
        canvas.style.height = `${vs.height / dpr}px`;
        canvas.style.display = 'block';
        canvas.style.margin = '0 auto';
        const ctx = canvas.getContext('2d', { alpha: false });
        await page.render({ canvasContext: ctx, viewport: vs, background: '#fff' }).promise;
        if (!cancelled) setScale(s);
      } catch (err) {
        if (err.name !== 'RenderingCancelledException') console.error(err);
      }
    })();
    
    return () => { cancelled = true; };
  }, [pdfDoc, pageNumber]);

  const getImageUrl = useCallback((page, q = quality) => {
    const id = mediaId || 292;
    return `/api/v1/products/pdf-to-image/${id}/?page=${page}&quality=${q}`;
  }, [mediaId, quality]);

  const changePage = (offset) => setPageNumber(prev => Math.max(1, Math.min(prev + offset, numPages)));
  const goToPage = (page) => setPageNumber(Math.max(1, Math.min(page, numPages)));
  const zoomIn = () => setScale(prev => Math.min(prev + 0.2, 2.5));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.5));
  
  // ✅ زوم تصویر
  const imageZoomIn = () => setImageScale(prev => Math.min(prev + 0.2, 2.5));
  const imageZoomOut = () => setImageScale(prev => Math.max(prev - 0.2, 0.5));
  
  const cycleQuality = () => {
    const qualities = Object.keys(QUALITY_LEVELS);
    setQuality(qualities[(qualities.indexOf(quality) + 1) % qualities.length]);
  };

  const handleContextMenu = (e) => e.preventDefault();

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'p')) e.preventDefault();
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); changePage(1); }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); changePage(-1); }
      if (isScannedPDF && (e.key === '+' || e.key === '=')) { e.preventDefault(); imageZoomIn(); }
      if (isScannedPDF && e.key === '-') { e.preventDefault(); imageZoomOut(); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [changePage]);

  useEffect(() => { return () => { if (renderTaskRef.current) renderTaskRef.current.cancel(); }; }, []);

  if (urlError || error) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center" style={{ fontFamily: 'Vazirmatn' }}>
        <p className="text-red-500 mb-4">{error || urlError}</p>
        <button onClick={() => { setError(null); refetchMediaUrl?.(); }} className="px-4 py-2 bg-teal-600 text-white rounded-lg">تلاش مجدد</button>
      </div>
    );
  }

  if (isScannedPDF === null || loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center" style={{ fontFamily: 'Vazirmatn' }}>
        <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ==================== Canvas ====================
  if (!isScannedPDF) {
    return (
      <div className="h-screen flex flex-col bg-gray-100 select-none" onContextMenu={handleContextMenu} dir="ltr">
        <div className="bg-white border-b-2 border-teal-700 shadow-sm px-4 py-3 flex items-center justify-between" style={{ fontFamily: 'Vazirmatn' }}>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-teal-700 text-xl">←</button>
            <h2 className="text-teal-800 font-bold text-sm truncate max-w-[200px]">{headerTitle}</h2>
          </div>
          <span className="text-teal-600 text-xs">{toPersian(pageNumber)} / {toPersian(numPages)}</span>
        </div>
        <div className="bg-white border-b px-4 py-2 flex items-center justify-center gap-2" style={{ fontFamily: 'Vazirmatn' }}>
          <button onClick={zoomOut} className="w-8 h-8 rounded-full bg-gray-100 text-teal-700 text-sm">−</button>
          <span className="text-xs text-teal-700 w-10 text-center">{Math.round(scale * 100)}%</span>
          <button onClick={zoomIn} className="w-8 h-8 rounded-full bg-gray-100 text-teal-700 text-sm">+</button>
          <div className="w-px h-5 bg-gray-300 mx-1" />
          <button onClick={() => changePage(-1)} disabled={pageNumber <= 1} className="w-8 h-8 rounded-full bg-gray-100 text-teal-700 text-sm disabled:opacity-30">◀</button>
          <input type="number" min={1} max={numPages} value={pageNumber} onChange={(e) => goToPage(parseInt(e.target.value) || 1)} className="w-10 h-8 text-center border rounded text-xs text-teal-700" style={{ fontFamily: 'Vazirmatn' }} />
          <button onClick={() => changePage(1)} disabled={pageNumber >= numPages} className="w-8 h-8 rounded-full bg-gray-100 text-teal-700 text-sm disabled:opacity-30">▶</button>
        </div>
        <div ref={containerRef} className="flex-1 overflow-auto flex justify-center p-2 bg-gray-50">
          <canvas ref={canvasRef} />
        </div>
      </div>
    );
  }

  // ==================== Image ====================
  const imageUrl = getImageUrl(pageNumber, quality);

  return (
    <div className="h-screen flex flex-col bg-gray-50 select-none" onContextMenu={handleContextMenu} dir="ltr">
      <div className="bg-white border-b-2 border-teal-700 shadow-sm px-4 py-3 flex items-center justify-between" style={{ fontFamily: 'Vazirmatn' }}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-teal-700 text-xl">←</button>
          <h2 className="text-teal-800 font-bold text-sm truncate max-w-[200px]">{headerTitle}</h2>
        </div>
        <span className="text-teal-600 text-xs">{toPersian(pageNumber)} / {toPersian(numPages)}</span>
      </div>
      <div className="bg-white border-b px-4 py-2 flex items-center justify-center gap-2" style={{ fontFamily: 'Vazirmatn' }}>
        {/* ✅ زوم تصویر */}
        <button onClick={imageZoomOut} className="w-8 h-8 rounded-full bg-gray-100 text-teal-700 text-sm">−</button>
        <span className="text-xs text-teal-700 w-10 text-center">{Math.round(imageScale * 100)}%</span>
        <button onClick={imageZoomIn} className="w-8 h-8 rounded-full bg-gray-100 text-teal-700 text-sm">+</button>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <button onClick={cycleQuality} className="px-2 py-1 text-xs rounded bg-gray-100 text-teal-700">{QUALITY_LEVELS[quality]?.label}</button>
        <span className="text-xs text-gray-400">{QUALITY_LEVELS[quality]?.dpi} DPI</span>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <button onClick={() => changePage(-1)} disabled={pageNumber <= 1} className="w-8 h-8 rounded-full bg-gray-100 text-teal-700 text-sm disabled:opacity-30">◀</button>
        <input type="number" min={1} max={numPages} value={pageNumber} onChange={(e) => goToPage(parseInt(e.target.value) || 1)} className="w-10 h-8 text-center border rounded text-xs text-teal-700" style={{ fontFamily: 'Vazirmatn' }} />
        <button onClick={() => changePage(1)} disabled={pageNumber >= numPages} className="w-8 h-8 rounded-full bg-gray-100 text-teal-700 text-sm disabled:opacity-30">▶</button>
      </div>
      {/* ✅ تصویر با زوم */}
      <div ref={containerRef} className="flex-1 overflow-auto flex justify-center bg-gray-100 p-2">
        <img 
          src={imageUrl} 
          alt={`صفحه ${pageNumber}`} 
          style={{ transform: `scale(${imageScale})`, transformOrigin: 'top center', transition: 'transform 0.2s' }}
          crossOrigin="use-credentials"
          onError={() => setError('خطا در بارگذاری تصویر')} 
        />
      </div>
    </div>
  );
};

export default PDFViewer;